import React, { useState } from 'react';
import { Mail, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import useReveal from '../hooks/useReveal';
import { sendContact } from '../lib/api';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const topics = ['1:1 Product Mentorship', 'Technology & AI Courses', 'Strategy Session', 'Speaking Engagement', 'Corporate Training', 'Something else'];

export default function Contact() {
  const ref = useReveal();
  const [form, setForm] = useState({ name: '', email: '', topic: '', message: '' });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in your name, email and message.');
      return;
    }
    setLoading(true);
    try {
      await sendContact(form);
      toast.success("Thanks! Rajni's team will be in touch soon.");
      setForm({ name: '', email: '', topic: '', message: '' });
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={ref} className="max-w-6xl mx-auto px-5 sm:px-8 pt-12">
      <div className="grid md:grid-cols-[0.9fr_1.1fr] gap-12">
        <div className="reveal in">
          <span className="eyebrow text-[#2E6BFF]">Start a conversation</span>
          <h1 className="font-display font-extrabold text-[#0E1726] text-[40px] sm:text-[50px] tracking-tight leading-[1.02] mt-4">
            You don't need another generic framework.
          </h1>
          <p className="text-[#5B6472] text-[17px] leading-relaxed mt-5">
            You need the right next move. Tell me what you are trying to change, and we'll find the best place to begin.
          </p>
          <div className="mt-8 space-y-4">
            <div className="flex items-center gap-3 text-[#0E1726]">
              <span className="w-10 h-10 rounded-full bg-white border border-black/10 flex items-center justify-center"><Mail size={17} className="text-[#2E6BFF]" /></span>
              <div>
                <a href="mailto:hello@bytesizepm.com" className="font-medium hover:text-[#2E6BFF]">hello@bytesizepm.com</a>
                <div className="text-sm text-[#5B6472]">General &amp; coaching</div>
              </div>
            </div>
            <div className="flex items-center gap-3 text-[#0E1726]">
              <span className="w-10 h-10 rounded-full bg-white border border-black/10 flex items-center justify-center"><Mail size={17} className="text-[#2E6BFF]" /></span>
              <div>
                <a href="mailto:inquiry@bytesizepm.com" className="font-medium hover:text-[#2E6BFF]">inquiry@bytesizepm.com</a>
                <div className="text-sm text-[#5B6472]">Speaking &amp; corporate training</div>
              </div>
            </div>
            <div className="flex items-center gap-3 text-[#0E1726]">
              <span className="w-10 h-10 rounded-full bg-white border border-black/10 flex items-center justify-center"><MapPin size={17} className="text-[#2E6BFF]" /></span>
              San Francisco Bay Area
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="reveal in bg-white rounded-[24px] p-7 sm:p-9 shadow-[0_30px_80px_-45px_rgba(14,23,38,0.4)]">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-[#0E1726]">Name</label>
              <Input className="mt-1.5 h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#0E1726]">Email</label>
              <Input className="mt-1.5 h-11" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-sm font-semibold text-[#0E1726]">What are you interested in?</label>
            <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
              <SelectTrigger className="mt-1.5 h-11"><SelectValue placeholder="Choose a topic" /></SelectTrigger>
              <SelectContent>
                {topics.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4">
            <label className="text-sm font-semibold text-[#0E1726]">What are you trying to change?</label>
            <Textarea className="mt-1.5 min-h-[130px]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell me a little about your situation..." />
          </div>
          <button type="submit" disabled={loading} data-cta="Contact" className="mt-6 w-full bg-[#0E1B2E] text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 hover:bg-[#1b2a44] transition-colors disabled:opacity-60">
            {loading ? 'Sending...' : 'Send message'} <Send size={17} />
          </button>
        </form>
      </div>
    </div>
  );
}
